// 📄 pages/SAP-BTP/components/SelectorPerfil.tsx
// "Desde perfil" (docs/plans/plan-perfiles-valores.md, P2): buscador para
// insertar en un campo un valor guardado en un perfil de valores. Se usa en
// "Adaptar valores" y en la edición en línea del diagrama.
//
// Cada perfil abierto es una llamada a KMS: se abre uno a la vez (por defecto
// el último usado en esta organización) y se recuerda en memoria mientras la
// página está abierta (nunca en el navegador).
import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound, RefreshCw, Search, X } from 'lucide-react';
import {
  PerfilValores, ResumenPerfil, TIPOS_VALOR_PERFIL, TipoValorPerfil, listarPerfiles, obtenerPerfil
} from '../../../services/perfilValoresService.ts';

interface SelectorPerfilProps {
  orgId: string;
  // Tipo que sugiere el campo (ej. una dirección -> URL); se puede ver todo igual.
  tipoSugerido?: TipoValorPerfil | null;
  onElegir: (valor: string) => void;
  onCerrar: () => void;
}

// Memoria de la página: perfiles ya descifrados (clave orgId/id).
const cache = new Map<string, PerfilValores>();
/** Olvida lo que hay en memoria (al editar o borrar un perfil en su ventana). */
export const olvidarPerfilesEnMemoria = () => cache.clear();

/** Un perfil con sus valores, desde la memoria de la página o descifrado (una llamada a KMS). */
export const cargarPerfil = async (orgId: string, id: string): Promise<PerfilValores> => {
  const clave = `${orgId}/${id}`;
  const enMemoria = cache.get(clave);
  if (enMemoria) return enMemoria;
  const p = await obtenerPerfil(orgId, id);
  cache.set(clave, p);
  return p;
};

const claveUltimo = (orgId: string) => `onehub.perfilValores.ultimo.${orgId}`;
const leerUltimo = (orgId: string) => {
  try { return localStorage.getItem(claveUltimo(orgId)) || ''; } catch { return ''; }
};
const guardarUltimo = (orgId: string, id: string) => {
  try { localStorage.setItem(claveUltimo(orgId), id); } catch { /* sin almacenamiento: no pasa nada */ }
};
// También los usan "Aplicar perfil" y "Guardar en un perfil" (P3).
export { leerUltimo as ultimoPerfilUsado, guardarUltimo as recordarPerfilUsado };

/** Qué tipo de valor de perfil corresponde a un campo de adaptador. */
export const tipoParaCampo = (campo: string): TipoValorPerfil | null =>
  ['address', 'httpAddressWithoutQuery', 'url', 'host', 'proxyHost'].includes(campo) ? 'url'
    : campo === 'httpAddressQuery' ? 'filtro'
    : ['credentialName', 'privateKeyAlias'].includes(campo) ? 'alias'
    : campo === 'location_id' ? 'location'
    : null;

const etiquetaTipo = (t: string) => TIPOS_VALOR_PERFIL.find((x) => x.value === t)?.label || t;

export default function SelectorPerfil({ orgId, tipoSugerido = null, onElegir, onCerrar }: SelectorPerfilProps) {
  const [perfiles, setPerfiles] = useState<ResumenPerfil[] | null>(null);
  const [perfilId, setPerfilId] = useState('');
  const [perfil, setPerfil] = useState<PerfilValores | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [soloSugerido, setSoloSugerido] = useState(!!tipoSugerido);

  useEffect(() => {
    listarPerfiles(orgId)
      .then((lista) => {
        setPerfiles(lista);
        const ultimo = leerUltimo(orgId);
        setPerfilId(lista.some((p) => p.id === ultimo) ? ultimo : lista[0]?.id || '');
      })
      .catch((err) => { setError(err?.message || 'No se pudieron cargar los perfiles.'); setPerfiles([]); });
  }, [orgId]);

  useEffect(() => {
    if (!perfilId) { setPerfil(null); return; }
    let vigente = true;
    setCargando(true);
    setError(null);
    cargarPerfil(orgId, perfilId)
      .then((p) => { if (vigente) setPerfil(p); })
      .catch((err) => { if (vigente) setError(err?.message || 'No se pudo abrir el perfil.'); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [orgId, perfilId]);

  // Esc cierra el buscador (sin cerrar lo que está detrás).
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onCerrar(); } };
    window.addEventListener('keydown', alTeclear, true);
    return () => window.removeEventListener('keydown', alTeclear, true);
  }, [onCerrar]);

  const valores = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (perfil?.valores || [])
      .filter((v) => v.valor)
      .filter((v) => !soloSugerido || !tipoSugerido || v.tipo === tipoSugerido)
      .filter((v) => !t || [v.nombre, v.valor].some((x) => x.toLowerCase().includes(t)));
  }, [perfil, texto, soloSugerido, tipoSugerido]);

  const elegir = (valor: string) => {
    if (perfilId) guardarUltimo(orgId, perfilId);
    onElegir(valor);
  };

  const inputCls = 'bg-zinc-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50';

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onCerrar}>
      <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-3 p-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-400" /> Insertar desde un perfil</h3>
          <button onClick={onCerrar} className="text-gray-400 hover:text-white" aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </header>

        {perfiles === null ? (
          <div className="p-8 text-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : perfiles.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 text-center">
            No tienes perfiles en esta organización. Créalos en el Repositorio, con el botón "Perfiles de valores".
          </p>
        ) : (
          <>
            <div className="p-4 space-y-2 border-b border-white/10">
              <div className="flex flex-col sm:flex-row gap-2">
                <select className={`${inputCls} sm:w-56`} value={perfilId} onChange={(e) => { setPerfilId(e.target.value); setTexto(''); }} aria-label="Perfil">
                  {perfiles.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.ambiente ? ` (${p.ambiente})` : ''}</option>)}
                </select>
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input autoFocus value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar por nombre o valor…" className={`${inputCls} w-full pl-7`} />
                </div>
              </div>
              {tipoSugerido && (
                <label className="inline-flex items-center gap-2 text-[11px] text-gray-400">
                  <input type="checkbox" checked={soloSugerido} onChange={(e) => setSoloSugerido(e.target.checked)} />
                  Solo de tipo "{etiquetaTipo(tipoSugerido)}" (el que corresponde a este campo)
                </label>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {error && <p className="m-2 text-xs text-red-400">{error}</p>}
              {cargando ? (
                <div className="p-6 text-center text-gray-400"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : valores.length === 0 ? (
                <p className="p-4 text-xs text-gray-500 text-center">
                  {soloSugerido && tipoSugerido ? `Este perfil no tiene valores de tipo "${etiquetaTipo(tipoSugerido)}". Desmarca el filtro para ver todos.` : 'Ningún valor coincide.'}
                </p>
              ) : (
                valores.map((v) => (
                  <button key={v.id} onClick={() => elegir(v.valor)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-cyan-500/10 border border-transparent hover:border-cyan-500/30">
                    <p className="text-xs text-white">{v.nombre} <span className="text-gray-500">· {etiquetaTipo(v.tipo)}</span></p>
                    <p className="text-xs font-mono text-gray-300 break-all">{v.valor}</p>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
